'use client'

import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { useLanguage } from '@/context/LanguageContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Icons } from '@/components/ui/icons'
import { toast } from '@/components/ui/toast'

interface HealthCentre {
  place_id: string
  name: string
  address: string
  latitude: number
  longitude: number
  distance_km: number
  type_label: string
  phone?: string | null
  services: string[]
}

type TypeFilter = 'all' | 'asha' | 'anganwadi' | 'ngo'

const DEFAULT_LAT = 19.076
const DEFAULT_LNG = 72.8777

const FILTER_OPTIONS: { value: TypeFilter; labelKey: string }[] = [
  { value: 'all', labelKey: 'centres.filter_all' },
  { value: 'anganwadi', labelKey: 'centres.filter_anganwadi' },
  { value: 'asha', labelKey: 'centres.filter_asha' },
  { value: 'ngo', labelKey: 'centres.filter_ngo' },
]

function typeBadgeClass(typeLabel: string): string {
  const lower = typeLabel.toLowerCase()
  if (lower.includes('anganwadi')) return 'bg-emerald-100 text-emerald-700'
  if (lower.includes('ngo')) return 'bg-purple-100 text-purple-700'
  if (lower.includes('asha')) return 'bg-blue-100 text-blue-700'
  return 'bg-gray-100 text-gray-700'
}

export default function NearbyCentresPage() {
  const { t, language } = useLanguage()

  const [latitude, setLatitude] = useState<number | null>(null)
  const [longitude, setLongitude] = useState<number | null>(null)
  const [manualLat, setManualLat] = useState('')
  const [manualLng, setManualLng] = useState('')
  const [radiusKm, setRadiusKm] = useState(10)
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [centres, setCentres] = useState<HealthCentre[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [locationReady, setLocationReady] = useState(false)
  const [usedManualLocation, setUsedManualLocation] = useState(false)

  useEffect(() => {
    if (!navigator.geolocation) {
      setUsedManualLocation(true)
      setManualLat(String(DEFAULT_LAT))
      setManualLng(String(DEFAULT_LNG))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude)
        setLongitude(position.coords.longitude)
        setLocationReady(true)
      },
      () => {
        setUsedManualLocation(true)
        setManualLat(String(DEFAULT_LAT))
        setManualLng(String(DEFAULT_LNG))
        toast.info('Please allow location access to find centres near you, or enter coordinates manually.')
      },
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }, [])

  const resolveCoordinates = (): { lat: number; lng: number } | null => {
    if (usedManualLocation || latitude === null || longitude === null) {
      const lat = parseFloat(manualLat)
      const lng = parseFloat(manualLng)
      if (Number.isNaN(lat) || Number.isNaN(lng)) return null
      return { lat, lng }
    }
    return { lat: latitude, lng: longitude }
  }

  const handleSearch = async () => {
    const coords = resolveCoordinates()
    if (!coords) {
      toast.error('Please enter valid latitude and longitude coordinates.')
      return
    }

    setIsSearching(true)
    setCentres([])
    setHasSearched(true)

    try {
      const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
      const params = new URLSearchParams({
        latitude: String(coords.lat),
        longitude: String(coords.lng),
        radius_km: String(radiusKm),
        type_filter: typeFilter,
        lang: language,
      })

      const response = await fetch(
        `${apiUrl}${basePath}/api/health-centres/search?${params.toString()}`
      )

      if (!response.ok) {
        throw new Error('Search failed')
      }

      const data: HealthCentre[] = await response.json()
      setCentres(data)

      if (data.length === 0) {
        toast.warning(t('centres.no_results'))
      }
    } catch (err) {
      console.error(err)
      toast.error('Failed to search nearby centres. Please try again.')
    } finally {
      setIsSearching(false)
    }
  }

  const openDirections = (centre: HealthCentre) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${centre.latitude},${centre.longitude}`
    window.open(url, '_blank')
  }

  return (
    <div className='mx-auto max-w-4xl space-y-8 pb-16'>
      <div className='py-4 text-center'>
        <h1 className='text-display-3 font-bold text-brand-navy lg:text-display-2'>
          {t('centres.title')}
        </h1>
        <p className='mx-auto mt-4 max-w-2xl text-base text-muted-foreground'>
          {t('centres.subtitle')}
        </p>
      </div>

      <Card className='relative overflow-hidden'>
        <CardWatermark opacity={2} scale={1.1} />
        <CardHeader className='pb-3'>
          <CardTitle className='flex items-center gap-2 text-lg font-semibold'>
            <Icons.mapPin className='h-5 w-5 text-brand-cornflower' strokeWidth={1.5} />
            {t('centres.search_btn')}
          </CardTitle>
          <CardDescription>
            {locationReady && !usedManualLocation
              ? `GPS location detected (${latitude?.toFixed(4)}, ${longitude?.toFixed(4)})`
              : t('centres.permission_prompt')}
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-5'>
          {(usedManualLocation || !locationReady) && (
            <div className='grid gap-3 sm:grid-cols-2'>
              <div>
                <label htmlFor='manual-lat' className='text-micro mb-1 block text-brand-muted'>
                  Latitude
                </label>
                <input
                  id='manual-lat'
                  type='number'
                  step='any'
                  value={manualLat}
                  onChange={(e) => {
                    setUsedManualLocation(true)
                    setManualLat(e.target.value)
                  }}
                  placeholder='19.0760'
                  className='w-full rounded-full border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-brand-cornflower/50 focus:ring-2 focus:ring-brand-cornflower/20'
                />
              </div>
              <div>
                <label htmlFor='manual-lng' className='text-micro mb-1 block text-brand-muted'>
                  Longitude
                </label>
                <input
                  id='manual-lng'
                  type='number'
                  step='any'
                  value={manualLng}
                  onChange={(e) => {
                    setUsedManualLocation(true)
                    setManualLng(e.target.value)
                  }}
                  placeholder='72.8777'
                  className='w-full rounded-full border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-brand-cornflower/50 focus:ring-2 focus:ring-brand-cornflower/20'
                />
              </div>
            </div>
          )}

          <div>
            <label htmlFor='radius-slider' className='text-micro mb-2 block text-brand-muted'>
              {t('centres.slider_label')}: {radiusKm} km
            </label>
            <input
              id='radius-slider'
              type='range'
              min={1}
              max={50}
              step={1}
              value={radiusKm}
              onChange={(e) => setRadiusKm(Number(e.target.value))}
              className='w-full accent-brand-cornflower'
            />
            <div className='mt-1 flex justify-between text-xs text-muted-foreground'>
              <span>1 km</span>
              <span>50 km</span>
            </div>
          </div>

          <div className='flex flex-wrap gap-2'>
            {FILTER_OPTIONS.map((option) => (
              <Button
                key={option.value}
                variant={typeFilter === option.value ? 'default' : 'outline'}
                size='sm'
                className='rounded-full'
                onClick={() => setTypeFilter(option.value)}
              >
                {t(option.labelKey)}
              </Button>
            ))}
          </div>

          <Button
            variant='gradient'
            size='lg'
            className='w-full'
            onClick={handleSearch}
            disabled={isSearching}
          >
            {isSearching ? (
              <>
                <Icons.loader className='mr-2 h-5 w-5 animate-spin' strokeWidth={1.5} />
                {t('centres.searching')}
              </>
            ) : (
              <>
                <Icons.search className='mr-2 h-5 w-5' strokeWidth={1.5} />
                {t('centres.search_btn')}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <AnimatePresence>
        {centres.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className='space-y-4'
          >
            {centres.map((centre) => (
              <Card key={centre.place_id} className='relative overflow-hidden bg-white'>
                <CardWatermark opacity={2} scale={0.8} />
                <CardContent className='relative z-10 space-y-3 p-6'>
                  <div className='flex flex-wrap items-start justify-between gap-3'>
                    <div className='min-w-0 flex-1'>
                      <h3 className='font-bold text-brand-navy'>{centre.name}</h3>
                      <p className='mt-1 text-sm text-muted-foreground'>{centre.address}</p>
                    </div>
                    <span
                      className={cn(
                        'inline-flex shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                        typeBadgeClass(centre.type_label)
                      )}
                    >
                      {centre.type_label}
                    </span>
                  </div>

                  <div className='flex flex-wrap items-center gap-3 text-sm text-muted-foreground'>
                    <span className='flex items-center gap-1 font-semibold text-brand-navy'>
                      <Icons.mapPin className='h-4 w-4 text-brand-cornflower' strokeWidth={1.5} />
                      {centre.distance_km} km {t('centres.distance')}
                    </span>
                    {centre.phone && (
                      <span>{centre.phone}</span>
                    )}
                  </div>

                  {centre.services.length > 0 && (
                    <div className='flex flex-wrap gap-1.5'>
                      {centre.services.map((service) => (
                        <span
                          key={service}
                          className='rounded-full border border-gray-100 bg-gray-50 px-2 py-0.5 text-[10px] text-brand-navy'
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                  )}

                  <Button
                    variant='outline'
                    size='sm'
                    className='gap-2'
                    onClick={() => openDirections(centre)}
                  >
                    <Icons.externalLink className='h-4 w-4' strokeWidth={1.5} />
                    {t('centres.get_directions')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {!isSearching && hasSearched && centres.length === 0 && (
        <p className='text-center text-sm text-muted-foreground'>
          {t('centres.no_results')}
        </p>
      )}
    </div>
  )
}
